import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { fetchAdminGoogleMapsApiKey, loadGoogleMapsAdminSdk } from '../google-maps-sdk.js';

function gmapsFromLL(v) {
    if (!v || v.lat == null || v.lng == null) return '#';
    return `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lng}`;
}
function gdirsFromLL(v) {
    if (!v || v.lat == null || v.lng == null) return '#';
    return `https://www.google.com/maps/dir/?api=1&destination=${v.lat}%2C${v.lng}`;
}
function fmtDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' });
}

/** Module-level helper — safe for Alpine :attr / x-text bindings. */
function venueViewDisplayName(venue) {
    return String(venue?.name || '').trim() || 'Unnamed venue';
}

function normalizeVenueRecord(data, routeId) {
    const nested = data?.venue && typeof data.venue === 'object' ? data.venue : null;
    const raw = nested || data || {};
    const id = String(raw._id || raw.id || data?._id || data?.id || routeId || '').trim();
    const latRaw = raw.lat ?? data?.lat;
    const lngRaw = raw.lng ?? data?.lng;
    const lat = latRaw != null && latRaw !== '' ? Number(latRaw) : null;
    const lng = lngRaw != null && lngRaw !== '' ? Number(lngRaw) : null;
    const name = String(raw.name || data?.name || '').trim();
    return {
        ...raw,
        _id: id || raw._id || raw.id,
        id: id || raw.id || raw._id,
        name: name || 'Unnamed venue',
        lat: Number.isFinite(lat) ? lat : null,
        lng: Number.isFinite(lng) ? lng : null,
        active: raw.active !== false && data?.active !== false,
    };
}

Alpine.data('venuesView', () => ({

    loading: true,
    error: '',
    venue: null,
    map: null,
    marker: null,
    mapsBlocked: false,
    mapsBlockedReason: '',

    get hasLatLng() {
        return !!(this.venue && Number.isFinite(this.venue.lat) && Number.isFinite(this.venue.lng));
    },

    gmapsFromLL, gdirsFromLL, fmtDate,

    readRouteId() {
        const path = location.pathname;
        const parts = path.split('/').filter(Boolean);
        const idx = parts.indexOf('venues');
        if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
        return null;
    },

    editUrl() {
        const id = String(this.venue?._id || this.venue?.id || '').trim();
        if (!id) return '/admin/venues';
        return `/admin/venues/${encodeURIComponent(id)}/edit`;
    },

    mapsBlockedTitle() {
        if (this.mapsBlockedReason === 'invalid') return 'Google Maps key rejected';
        if (this.mapsBlockedReason === 'load_failed') return 'Google Maps could not load';
        return 'Google Maps API key not configured';
    },

    mapsBlockedDetail() {
        if (this.mapsBlockedReason === 'invalid') {
            return 'The key was refused by Google. Check billing and HTTP referrer restrictions in Google Cloud Console.';
        }
        if (this.mapsBlockedReason === 'load_failed') {
            return 'The map script failed to load or timed out.';
        }
        return 'Configure a Maps JavaScript API key in Account settings → Subscription (Google Maps API).';
    },

    async init() {
        try {
            const id = this.readRouteId();
            if (!id) throw new Error('No venue id in route.');
            const res = await apiFetch(`/venues/${encodeURIComponent(id)}`);
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e.message || 'Failed to load venue');
            }
            const data = await res.json();
            this.venue = normalizeVenueRecord(data, id);
            document.title = `${venueViewDisplayName(this.venue)} — DartsOS`;

            if (this.hasLatLng) {
                let apiKey = '';
                try {
                    apiKey = await fetchAdminGoogleMapsApiKey();
                } catch {
                    apiKey = '';
                }
                if (!apiKey) {
                    this.mapsBlocked = true;
                    this.mapsBlockedReason = 'no_key';
                } else {
                    try {
                        await loadGoogleMapsAdminSdk(apiKey, { libraries: '' });
                        this.mapsBlocked = false;
                        this.mapsBlockedReason = '';
                    } catch (e) {
                        this.mapsBlocked = true;
                        const code = e?.code || (e?.message === 'auth_failed' ? 'AUTH_FAILED' : '');
                        this.mapsBlockedReason = code === 'AUTH_FAILED' || e?.message === 'auth_failed' ? 'invalid' : 'load_failed';
                    }
                }
            }

            this.$nextTick(() => {
                if (this.hasLatLng && !this.mapsBlocked) this.initMap();
            });
        } catch (e) {
            this.error = e.message || 'Failed to load.';
        } finally {
            this.loading = false;
        }
    },

    initMap() {
        if (this.mapsBlocked) return;
        if (!(window.google && window.google.maps)) {
            const t = setInterval(() => {
                if (window.google && window.google.maps) {
                    clearInterval(t);
                    this.initMap();
                }
            }, 150);
            return;
        }
        const el = document.getElementById('venue-map');
        if (!el) return;
        const center = { lat: Number(this.venue.lat), lng: Number(this.venue.lng) };
        this.map = new google.maps.Map(el, {
            center,
            zoom: 16,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
        });
        this.marker = new google.maps.Marker({ position: center, map: this.map });
    },

}));
